import { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  ArrowUpRight, Sparkles, Check, Briefcase, MapPin, DollarSign, BookOpen,
  Film, Disc3, Podcast, Users, MessageCircle, UserPlus, Lock, Flame,
} from "lucide-react";
import axios from "axios";
import { useAuth } from "@/store/AuthContext";
import { useLibrary } from "@/lib/hooks";
import {
  PREMIUM_JOBS, PREMIUM_JOBS_HEADING, PREMIUM_JOBS_SUBHEAD,
  PREMIUM_MEDIA, PREMIUM_MEDIA_HEADING, PREMIUM_MEDIA_SUBHEAD,
  PREMIUM_FRIENDS, PREMIUM_FRIENDS_HEADING, PREMIUM_FRIENDS_SUBHEAD,
} from "@/lib/premiumMockData";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PERKS = [
  "Remove all sponsored placements",
  "Unlimited scheduled habits",
  "Start your own custom habits",
  "Export your shared calendar (ICS)",
  "Ritual-fit Roles · Kindred Three · Curated Media",
  "Priority matching for smaller circles",
];

const MEDIA_ICON = {
  Book: BookOpen, Film, Album: Disc3, Podcast,
};

// ==============================================================
export default function Premium() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [pendingRequests, setPendingRequests] = useState({});
  const { data: library = [] } = useLibrary({ category: "all", q: "" });
  const isPremium = !!user?.is_premium;

  // Map habit id → title (for the "linked to" pills)
  const habitTitle = useMemo(() => {
    const m = {};
    for (const h of library) m[h.id] = h.title;
    return m;
  }, [library]);

  const startCheckout = async () => {
    if (!user) {
      toast("Sign in first", { description: "We attach your upgrade to your account." });
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        package_id: "cadence_premium",
        origin_url: window.location.origin,
        user_id: user.user_id,
      });
      if (data?.checkout_url) window.location.href = data.checkout_url;
      else toast.error("Could not start checkout", { description: "Please try again." });
    } catch (e) {
      toast.error("Checkout failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  };

  const sendFriendRequest = (friend) => {
    if (friend.cycles_shared < 3) {
      toast("Not yet.", {
        description: `Share ${3 - friend.cycles_shared} more habit cycle${3 - friend.cycles_shared === 1 ? "" : "s"} with ${friend.name} first.`,
      });
      return;
    }
    setPendingRequests((r) => ({ ...r, [friend.id]: true }));
    toast("Friendship request sent.", {
      description: `${friend.name} will see it after their next habit closes.`,
    });
  };

  return (
    <div className="max-w-5xl mx-auto space-y-16" data-testid="premium-page">
      {/* -------- Hero + Perks -------- */}
      <header>
        <span className="eyebrow">Cadence Premium</span>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-stone-900 mt-2 leading-[1.05]">
          Fewer things. <span className="italic text-terracotta">More room.</span>
        </h1>
        <p className="text-stone-600 mt-4 leading-relaxed max-w-xl">
          An upgrade that quietly opens three doors: a job board tuned to your habits, media curated by your circle, and the three people most likely to become close friends.
        </p>
      </header>

      <section className="grid grid-cols-1 md:grid-cols-12 gap-6">
        <div className="md:col-span-7 card-editorial p-8 space-y-5" data-testid="premium-perks">
          <span className="eyebrow flex items-center gap-2"><Sparkles size={13} /> What you get</span>
          <ul className="space-y-3">
            {PERKS.map((p) => (
              <li key={p} className="flex items-start gap-3">
                <span className="mt-1 w-5 h-5 rounded-full bg-terracotta/10 text-terracotta flex items-center justify-center flex-shrink-0">
                  <Check size={12} strokeWidth={3} />
                </span>
                <span className="text-stone-800">{p}</span>
              </li>
            ))}
          </ul>
        </div>
        <aside className="md:col-span-5 card-editorial p-8 flex flex-col gap-4" data-testid="premium-price-card">
          <span className="eyebrow">One-time</span>
          <div className="flex items-baseline gap-2">
            <span className="font-serif text-6xl text-stone-900 leading-none">$5.99</span>
            <span className="text-sm text-stone-500">USD</span>
          </div>
          <p className="text-sm text-stone-600 leading-relaxed">
            A single payment. No renewal, no auto-charge. Cancel isn&apos;t a concept here.
          </p>
          {isPremium ? (
            <div className="btn-outline pointer-events-none justify-center" data-testid="premium-already-active">
              <Check size={14} /> You&apos;re already Premium
            </div>
          ) : (
            <button
              onClick={startCheckout}
              disabled={loading}
              className="btn-accent mt-2"
              data-testid="premium-checkout-btn"
            >
              {loading ? "Opening Stripe…" : (<>Upgrade with Stripe <ArrowUpRight size={16} /></>)}
            </button>
          )}
          <p className="text-[11px] text-stone-500 leading-relaxed">
            Test-mode sandbox · use card <span className="font-mono">4242 4242 4242 4242</span>, any future date, any CVC.
          </p>
        </aside>
      </section>

      {/* -------- Ritual-fit Roles -------- */}
      <section data-testid="premium-jobs-section">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <span className="eyebrow flex items-center gap-2"><Briefcase size={13} /> Premium service · roles</span>
            <h2 className="font-serif text-3xl sm:text-4xl text-stone-900 mt-1 leading-tight">
              {PREMIUM_JOBS_HEADING}
            </h2>
            <p className="text-sm text-stone-600 mt-2 max-w-2xl leading-relaxed">
              {PREMIUM_JOBS_SUBHEAD}
            </p>
          </div>
          {!isPremium && (
            <span className="tag-pill inline-flex items-center gap-1.5" data-testid="premium-jobs-locked-badge">
              <Lock size={11} /> Preview · unlock all with Premium
            </span>
          )}
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
          {PREMIUM_JOBS.map((j) => (
            <article
              key={j.id}
              className={`card-editorial p-6 flex flex-col gap-4 ${!isPremium ? "opacity-70" : ""}`}
              data-testid={`premium-job-${j.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-serif text-xl text-stone-900 leading-tight">{j.title}</p>
                  <p className="text-sm text-stone-600 mt-0.5">{j.company}</p>
                </div>
                <span className="tag-pill inline-flex items-center gap-1 whitespace-nowrap">
                  <DollarSign size={11} /> {j.salary}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-stone-500">
                <span className="inline-flex items-center gap-1"><MapPin size={12} /> {j.location}</span>
                {j.tags.map((t) => (
                  <span key={t} className="inline-flex items-center gap-1">· {t}</span>
                ))}
              </div>
              <div className="rounded-2xl bg-sage/10 border border-sage/20 p-3">
                <p className="eyebrow text-stone-600">Why it fits you</p>
                <p className="text-sm text-stone-800 mt-1 leading-relaxed">{j.why}</p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {j.linked_habits.map((hid) => (
                    <span
                      key={hid}
                      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest bg-terracotta/10 text-terracotta"
                    >
                      <Flame size={9} /> {habitTitle[hid] || hid}
                    </span>
                  ))}
                </div>
              </div>
              <div className="pt-2 border-t border-stone-100 flex items-center justify-between">
                <span className="eyebrow text-stone-500">Referenced against your rhythm</span>
                <button
                  disabled={!isPremium}
                  onClick={() => toast(`Opening ${j.company}…`, { description: "Application flow is a mock in this prototype." })}
                  className={isPremium ? "btn-outline text-xs !min-h-[2rem] !py-1" : "btn-outline text-xs !min-h-[2rem] !py-1 opacity-60 pointer-events-none"}
                  data-testid={`premium-job-apply-${j.id}`}
                >
                  {isPremium ? "View role" : "Locked"} <ArrowUpRight size={12} />
                </button>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* -------- Curated Media -------- */}
      <section data-testid="premium-media-section">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <span className="eyebrow flex items-center gap-2"><BookOpen size={13} /> Premium service · knowledge</span>
            <h2 className="font-serif text-3xl sm:text-4xl text-stone-900 mt-1 leading-tight">
              {PREMIUM_MEDIA_HEADING}
            </h2>
            <p className="text-sm text-stone-600 mt-2 max-w-2xl leading-relaxed">
              {PREMIUM_MEDIA_SUBHEAD}
            </p>
          </div>
          {!isPremium && (
            <span className="tag-pill inline-flex items-center gap-1.5" data-testid="premium-media-locked-badge">
              <Lock size={11} /> Preview
            </span>
          )}
        </div>

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {PREMIUM_MEDIA.map((m) => {
            const Icon = MEDIA_ICON[m.kind] || BookOpen;
            return (
              <article
                key={m.id}
                className={`card-editorial p-5 flex flex-col gap-3 ${!isPremium ? "opacity-70" : ""}`}
                data-testid={`premium-media-${m.id}`}
              >
                <div className="flex items-center justify-between">
                  <span className="tag-pill inline-flex items-center gap-1.5">
                    <Icon size={12} /> {m.kind}
                  </span>
                  <span className="data-mono text-xs text-stone-500">{m.circle_votes} in circle</span>
                </div>
                <div>
                  <p className="font-serif text-2xl text-stone-900 leading-tight">{m.title}</p>
                  <p className="text-xs text-stone-500 mt-0.5">{m.author}</p>
                </div>
                <div className="rounded-xl bg-stone-50 border border-stone-100 p-3">
                  <p className="text-xs text-stone-700 leading-relaxed">{m.why}</p>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {m.linked_habits.map((hid) => (
                      <span
                        key={hid}
                        className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[9px] font-semibold uppercase tracking-widest bg-terracotta/10 text-terracotta"
                      >
                        {habitTitle[hid] || hid}
                      </span>
                    ))}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* -------- Kindred Three (Friend Finder) -------- */}
      <section data-testid="premium-friends-section">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <span className="eyebrow flex items-center gap-2"><Users size={13} /> Premium service · people</span>
            <h2 className="font-serif text-3xl sm:text-4xl text-stone-900 mt-1 leading-tight">
              {PREMIUM_FRIENDS_HEADING}
            </h2>
            <p className="text-sm text-stone-600 mt-2 max-w-2xl leading-relaxed">
              {PREMIUM_FRIENDS_SUBHEAD}
            </p>
          </div>
          {!isPremium && (
            <span className="tag-pill inline-flex items-center gap-1.5" data-testid="premium-friends-locked-badge">
              <Lock size={11} /> Preview
            </span>
          )}
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-5">
          {PREMIUM_FRIENDS.map((f) => {
            const unlocked = f.cycles_shared >= 3;
            const sent = !!pendingRequests[f.id];
            return (
              <article
                key={f.id}
                className={`card-editorial p-6 flex flex-col items-start gap-4 ${!isPremium ? "opacity-70" : ""}`}
                data-testid={`premium-friend-${f.id}`}
              >
                <img
                  src={f.avatar}
                  alt={f.name}
                  className="w-16 h-16 rounded-full object-cover border-2 border-white shadow-md"
                  referrerPolicy="no-referrer"
                />
                <div>
                  <p className="font-serif text-2xl text-stone-900 leading-tight">{f.name}</p>
                  <p className="text-xs text-stone-500 mt-0.5">
                    {f.age} · {f.location} · <span className="text-terracotta">{f.overlap_pct}% match</span>
                  </p>
                </div>
                <p className="text-sm text-stone-700 italic leading-relaxed">&ldquo;{f.tagline}&rdquo;</p>

                <div className="flex flex-wrap gap-1.5">
                  {f.signals.map((s) => (
                    <span key={s} className="tag-pill text-[10px]">{s}</span>
                  ))}
                </div>

                <div className="w-full rounded-xl bg-stone-50 border border-stone-100 p-3">
                  <div className="flex items-center justify-between">
                    <span className="eyebrow text-stone-600">Habit cycles shared</span>
                    <span
                      className={`data-mono text-xs ${unlocked ? "text-sage" : "text-stone-500"}`}
                      data-testid={`premium-friend-cycles-${f.id}`}
                    >
                      {f.cycles_shared}/3
                    </span>
                  </div>
                  <div className="mt-2 h-1.5 w-full rounded-full bg-stone-200 overflow-hidden">
                    <div
                      className={`h-full ${unlocked ? "bg-sage" : "bg-terracotta"}`}
                      style={{ width: `${Math.min(100, (f.cycles_shared / 3) * 100)}%` }}
                    />
                  </div>
                </div>

                <div className="w-full flex flex-col gap-2 mt-auto">
                  <button
                    disabled={!isPremium}
                    onClick={() => toast(`Say hi to ${f.name}`, { description: "Direct messaging is a mock in this prototype." })}
                    className={isPremium ? "btn-outline w-full text-xs !min-h-[2.25rem] !py-1" : "btn-outline w-full text-xs !min-h-[2.25rem] !py-1 opacity-60 pointer-events-none"}
                    data-testid={`premium-friend-message-${f.id}`}
                  >
                    <MessageCircle size={13} /> Say hi
                  </button>
                  <button
                    disabled={!isPremium || sent}
                    onClick={() => sendFriendRequest(f)}
                    className={
                      !isPremium
                        ? "btn-outline w-full text-xs !min-h-[2.25rem] !py-1 opacity-60 pointer-events-none"
                        : unlocked
                        ? (sent ? "btn-outline w-full text-xs !min-h-[2.25rem] !py-1" : "btn-accent w-full text-xs !min-h-[2.25rem] !py-1")
                        : "btn-outline w-full text-xs !min-h-[2.25rem] !py-1"
                    }
                    data-testid={`premium-friend-request-${f.id}`}
                    title={
                      unlocked
                        ? "Send a friendship request"
                        : `Share ${3 - f.cycles_shared} more habit cycle${3 - f.cycles_shared === 1 ? "" : "s"} first`
                    }
                  >
                    {sent ? (
                      <><Check size={13} /> Request sent</>
                    ) : unlocked ? (
                      <><UserPlus size={13} /> Send friendship request</>
                    ) : (
                      <><Lock size={13} /> {3 - f.cycles_shared} more cycle{3 - f.cycles_shared === 1 ? "" : "s"} to unlock</>
                    )}
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      </section>
    </div>
  );
}
